import React, { useState, useEffect } from 'react';
import { Typography, Container, Button, Box, Paper, FormControl, InputLabel, Select, MenuItem, TextField, Switch, FormControlLabel } from '@mui/material';
import { useTheme } from './ThemeContext.tsx'; // Import useTheme
import MobileHeader from './components/MobileHeader'; // Import MobileHeader
import useMediaQuery from '@mui/material/useMediaQuery'; // Import useMediaQuery
import { useTheme as useMuiTheme } from '@mui/material/styles'; // Import useMuiTheme
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { auth } from './firebase.ts';
import { Theme } from './predefinedThemes'; // Import Theme interface

import { User } from 'firebase/auth';
import { SelectChangeEvent } from '@mui/material';

interface SettingsProps {
  user: User | null;
  handleSignOut: () => Promise<void>;
}

function Settings({ user, handleSignOut }: SettingsProps) {
  const { theme, setCurrentThemeName, predefinedThemes, customLogoUrl, customHeaderText, uploadLogo, updateCustomHeaderText, clearCustomLogo, clearCustomHeaderText, brandingOption, setBrandingOption, isSchedulePageVisible, setIsSchedulePageVisible } = useTheme();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [headerTextInput, setHeaderTextInput] = useState<string>(customHeaderText || '');

  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Update headerTextInput when customHeaderText changes from context
  useEffect(() => {
    setHeaderTextInput(customHeaderText || '');
  }, [customHeaderText]);

  const handleThemeSelectChange = (event: SelectChangeEvent<string>) => {
    setCurrentThemeName(event.target.value);
  };

  const handleBrandingOptionChange = (event: SelectChangeEvent<string>) => {
    setBrandingOption(event.target.value as 'default' | 'text' | 'logo');
  };

  const handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setLogoFile(event.target.files[0]);
    }
  };

  const handleUploadLogo = async () => {
    if (logoFile && brandingOption === 'logo') {
      try {
        await uploadLogo(logoFile);
        alert('Logo uploaded successfully!');
        setLogoFile(null); // Clear the file input
      } catch (error: any) { // Explicitly type error
        alert('Error uploading logo: ' + error.message);
      }
    } else {
      alert('Please select "Custom Logo" option before uploading.');
    }
  };

  const handleHeaderTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderTextInput(event.target.value);
  };

  const handleSaveHeaderText = () => {
    if (brandingOption === 'text') {
      updateCustomHeaderText(headerTextInput);
      alert('Header text saved successfully!');
    } else {
      alert('Please select "Custom Text" option before saving.');
    }
  };

  return (
    <Container sx={{ pt: 0, px: 0 }} maxWidth={false}>
      {isMobile && <MobileHeader title="Settings" />}

      <Container maxWidth="sm" sx={{ mt: isMobile ? '70px' : '30px', mb: 4 }}> {/* Adjusted mt */}
        {/* User Profile Section */}
        <Paper
          sx={{
            p: 3,
          mb: 3,
          backgroundColor: theme.colors.whiteCardBackground,
          borderRadius: theme.borderRadius,
          boxShadow: theme.boxShadow,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.darkText, mb: 2 }}>
          User Profile
        </Typography>
        {user && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ color: theme.colors.darkText }}>
              <strong>Name:</strong> {user.displayName}
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.darkText }}>
              <strong>Email:</strong> {user.email}
            </Typography>
          </Box>
        )}
        <Button
          variant="contained"
          onClick={handleSignOut}
          sx={{
            mt: 2,
            backgroundColor: `${theme.colors.primaryRed} !important`,
            color: `${theme.colors.whiteCardBackground} !important`,
            textTransform: 'none',
            borderRadius: theme.borderRadius,
            boxShadow: theme.boxShadow,
            padding: '8px 20px',
          }}
        >
          Sign Out
        </Button>
      </Paper>

      {/* Navigation Settings Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.colors.whiteCardBackground,
          borderRadius: theme.borderRadius,
          boxShadow: theme.boxShadow,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.darkText, mb: 2 }}>
          Navigation Settings
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isSchedulePageVisible}
              onChange={(event) => setIsSchedulePageVisible(event.target.checked)}
              name="schedulePageVisibility"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.colors.primaryRed,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 0, 0, 0.08)',
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.colors.primaryRed,
                },
              }}
            />
          }
          label={
            <Typography variant="body1" sx={{ color: theme.colors.darkText }}>
              Show Scheduled Tasks Page in Header
            </Typography>
          }
        />
      </Paper>

      {/* Theme Settings Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.colors.whiteCardBackground,
          borderRadius: theme.borderRadius,
          boxShadow: theme.boxShadow,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.darkText, mb: 2 }}>
          Theme Settings
        </Typography>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="theme-select-label" sx={{ color: theme.colors.lightText }}>Select Theme</InputLabel>
          <Select
            labelId="theme-select-label"
            id="theme-select"
            value={theme.name}
            label="Select Theme"
            onChange={handleThemeSelectChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.borderColor,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.darkText,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.primaryRed,
              },
              '& .MuiSelect-select': {
                color: theme.colors.darkText,
              },
              '& .MuiSvgIcon-root': {
                color: theme.colors.lightText,
              },
            }}
          >
            {predefinedThemes.map((predefinedTheme: Theme) => ( // Explicitly type predefinedTheme
              <MenuItem key={predefinedTheme.name} value={predefinedTheme.name}>
                {predefinedTheme.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* App Branding Section */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          backgroundColor: theme.colors.whiteCardBackground,
          borderRadius: theme.borderRadius,
          boxShadow: theme.boxShadow,
          border: `1px solid ${theme.colors.borderColor}`,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.darkText, mb: 2 }}>
          App Branding
        </Typography>

        <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
          <InputLabel id="branding-option-label" sx={{ color: theme.colors.lightText }}>Branding Option</InputLabel>
          <Select
            labelId="branding-option-label"
            id="branding-option"
            value={brandingOption}
            label="Branding Option"
            onChange={handleBrandingOptionChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.borderColor,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.darkText,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.colors.primaryRed,
              },
              '& .MuiSelect-select': {
                color: theme.colors.darkText,
              },
              '& .MuiSvgIcon-root': {
                color: theme.colors.lightText,
              },
            }}
          >
            <MenuItem value="default">Default (CheckifAI)</MenuItem>
            <MenuItem value="text">Custom Text</MenuItem>
            <MenuItem value="logo">Custom Logo</MenuItem>
          </Select>
        </FormControl>

        {brandingOption === 'text' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ color: theme.colors.darkText, mb: 1 }}>
              Custom Header Text
            </Typography>
            <TextField
              fullWidth
              label="Enter custom header text"
              variant="outlined"
              value={headerTextInput}
              onChange={handleHeaderTextChange}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: theme.colors.borderColor,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.colors.darkText,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.colors.primaryRed,
                  },
                },
                '& .MuiInputBase-input': {
                  color: theme.colors.darkText,
                },
                '& .MuiInputLabel-root': {
                  color: theme.colors.lightText,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.colors.primaryRed,
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleSaveHeaderText}
                sx={{
                  backgroundColor: `${theme.colors.primaryRed} !important`,
                  color: `${theme.colors.whiteCardBackground} !important`,
                  textTransform: 'none',
                  borderRadius: theme.borderRadius,
                  boxShadow: theme.boxShadow,
                  padding: '8px 20px',
                }}
              >
                Save Text
              </Button>
            </Box>
          </Box>
        )}

        {brandingOption === 'logo' && (
          <Box>
            <Typography variant="subtitle1" sx={{ color: theme.colors.darkText, mb: 1 }}>
              Custom Logo
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoFileChange}
              style={{ marginBottom: '16px', color: theme.colors.darkText }}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleUploadLogo}
                disabled={!logoFile}
                sx={{
                  backgroundColor: `${theme.colors.primaryRed} !important`,
                  color: `${theme.colors.whiteCardBackground} !important`,
                  textTransform: 'none',
                  borderRadius: theme.borderRadius,
                  boxShadow: theme.boxShadow,
                  padding: '8px 20px',
                }}
              >
                Upload Logo
              </Button>
              {customLogoUrl && (
                <Box sx={{ ml: 2 }}>
                  <img src={customLogoUrl} alt="Current Logo" style={{ maxWidth: '100px', maxHeight: '50px', border: `1px solid ${theme.colors.borderColor}` }} />
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setBrandingOption('default')}
            sx={{
              borderColor: `${theme.colors.primaryRed} !important`,
              color: `${theme.colors.primaryRed} !important`,
              textTransform: 'none',
              borderRadius: theme.borderRadius,
              boxShadow: theme.boxShadow,
              padding: '8px 20px',
            }}
          >
            Restore Default Branding
          </Button>
        </Box>
      </Paper>
      </Container>
    </Container>
  );
}

export default Settings;
